import React, { useEffect, useMemo, useState, useCallback } from 'react';
import Classes from './Hero2.module.css';
import { ArrowNextIcon, GasPumpIcon } from '../../../../../../../utils/Icons';
import { createSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import backend from '../../../../../../../api';

const TYPE_PARAM_MAP = {
  regular: 'regular',
  mid: 'Diesel',
  premium: 'premium',
};
const DEFAULT_BOOKING_PAUSE_LABEL = 'Service Update';
const DEFAULT_BOOKING_PAUSE_MESSAGE =
  "Dear customers, due to military obligations of the owner, we are pausing services until summer of 2027.";

// ——— helpers ———
function isSubscriptionActive(as) {
  if (!as) return false;
  const status = (as.status || 'none').toLowerCase();
  if (status === 'none' || status === 'canceled' || status === 'cancelled') return false;
  const now = Date.now();
  const startOk = as.current_period_start ? new Date(as.current_period_start).getTime() <= now : true;
  const endOk = as.current_period_end ? new Date(as.current_period_end).getTime() >= now : false;
  return startOk && endOk;
}

const fmtPerGalPlusFee = (num, fee) => {
  const base = `$${Number(num || 0).toFixed(2)}/Gallon`;
  const feeNum = Number(fee || 0);
  return feeNum > 0 ? `${base} + $${feeNum.toFixed(2)} Fee Per Fill Up` : base;
};

function within24Hours(createdAt) {
  if (!createdAt) return false;
  const t = new Date(createdAt).getTime();
  if (!Number.isFinite(t)) return false;
  return Date.now() - t <= 24 * 60 * 60 * 1000;
}

/** Simple inline modal (no extra deps) */
function OfferModal({ open, onClose, onConfirm, onViewMembership, remainingText }) {
  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.currentTarget === e.target) onClose?.();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 2000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 92vw)',
          background: '#fff',
          borderRadius: 14,
          boxShadow: '0 20px 60px rgba(0,0,0,.25)',
          padding: 18,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900 }}>🎁 Free Delivery for 24 Hours</h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              fontWeight: 800,
              cursor: 'pointer',
              opacity: 0.7,
            }}
          >
            ✕
          </button>
        </div>

        <p style={{ marginTop: 10, marginBottom: 10, color: '#374151', fontWeight: 600, lineHeight: 1.35 }}>
          You’re eligible for <strong>$0 delivery fee</strong> if you book within 24 hours of signing up.
          {remainingText ? (
            <>
              <br />
              <span style={{ fontWeight: 800 }}>Time left:</span> {remainingText}
            </>
          ) : null}
        </p>

        <div
          style={{
            background: '#f8fafc',
            border: '1px solid #eef0f4',
            borderRadius: 12,
            padding: 12,
            marginBottom: 12,
          }}
        >
          <div style={{ fontWeight: 900, marginBottom: 6, color: '#111827' }}>Why membership helps (after the offer):</div>
          <ul style={{ margin: 0, paddingLeft: 18, color: '#4b5563', fontWeight: 600, lineHeight: 1.45 }}>
            <li>Delivery fees waived on eligible plans</li>
            <li>Better pricing / predictable costs</li>
            <li>Faster repeat booking experience</li>
          </ul>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onViewMembership}
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid #e5e7eb',
              background: '#fff',
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            View Membership
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid transparent',
              background: 'var(--secondary-color)',
              color: '#fff',
              fontWeight: 900,
              cursor: 'pointer',
            }}
          >
            Book with Free Delivery
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Hero2() {
  const navigateTo = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const signupDate = user?.createdAt;

  const onboarding = useSelector((state) => state.companyInfo.info);
  const companyId = onboarding?._id;

  const [service, setService] = useState(null);
  const [bookingsPaused, setBookingsPaused] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  const [pauseLabel, setPauseLabel] = useState(DEFAULT_BOOKING_PAUSE_LABEL);
  const [pauseMessage, setPauseMessage] = useState(DEFAULT_BOOKING_PAUSE_MESSAGE);

  const isSubbed = isSubscriptionActive(user?.activeSubscription);
  const isNewUserOffer = !isSubbed && within24Hours(signupDate);

  // Offer popup state
  const [offerOpen, setOfferOpen] = useState(false);
  const [pendingType, setPendingType] = useState(null);

  useEffect(() => {
    (async () => {
      if (!companyId) return;
      try {
        const { data } = await backend.get(`/api/services/by-company/${companyId}`);
        if (data?.ok) {
          setService(data.data || null);
          const active = !!data?.servicePause?.active;
          setBookingsPaused(active);
          setShowNotice(!!(data?.servicePause?.showNotice ?? active));
          setPauseLabel(data?.servicePause?.label || DEFAULT_BOOKING_PAUSE_LABEL);
          setPauseMessage(data?.servicePause?.message || DEFAULT_BOOKING_PAUSE_MESSAGE);
        }
      } catch (err) {
        console.error('Hero2: fetch services error', err);
      }
    })();
  }, [companyId]);

  // Delivery fee rule:
  // - active membership => $0
  // - new user offer window => $0
  // - else configured (fallback 9.99)
  const deliveryFee = useMemo(() => {
    if (isSubbed) return 0;
    if (isNewUserOffer) return 0;
    return Number(service?.fees?.delivery ?? 9.99);
  }, [isSubbed, isNewUserOffer, service]);

  const prices = useMemo(() => {
    const reg = Number(service?.prices?.regular ?? 4.5);
    const mid = Number(service?.prices?.mid ?? 5.0);
    const prem = Number(service?.prices?.premium ?? 5.5);
    return {
      regular: fmtPerGalPlusFee(reg, deliveryFee),
      mid: fmtPerGalPlusFee(mid, deliveryFee),
      premium: fmtPerGalPlusFee(prem, deliveryFee),
    };
  }, [service, deliveryFee]);

  const remainingText = useMemo(() => {
    if (!signupDate || !isNewUserOffer) return '';
    const created = new Date(signupDate).getTime();
    const msLeft = Math.max(0, created + 24 * 60 * 60 * 1000 - Date.now());
    const h = Math.floor(msLeft / (60 * 60 * 1000));
    const m = Math.floor((msLeft % (60 * 60 * 1000)) / (60 * 1000));
    return `${h}h ${m}m`;
  }, [signupDate, isNewUserOffer]);

  const goToAddAppointment = useCallback(
    (internalType) => {
      const type = TYPE_PARAM_MAP[internalType] || internalType;

      // NOTE: no backend changes; this is just a frontend hint you can read later if needed
      const search = createSearchParams({
        type,
        promo: isNewUserOffer ? 'free_delivery_24h' : '',
      }).toString();

      navigateTo({ pathname: '/add-appointment', search });
    },
    [navigateTo, isNewUserOffer]
  );

  const handleNavigate = useCallback(
    (internalType) => {
      // If they’re non-member AND in 24h window => show popup before booking
      if (!isSubbed && isNewUserOffer) {
        setPendingType(internalType);
        setOfferOpen(true);
        return;
      }
      goToAddAppointment(internalType);
    },
    [isSubbed, isNewUserOffer, goToAddAppointment]
  );

  return (
    <div className={Classes.hero}>
      <div className={Classes.header}>
        <h5>Services</h5>
        <button onClick={() => navigateTo('/services')}>View All {'>'}</button>
      </div>

      {showNotice && (
        <div
          style={{
            marginTop: 10,
            marginBottom: 12,
            padding: '12px 14px',
            borderRadius: 12,
            border: '1px solid #fed7aa',
            background: '#fff7ed',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 900, color: '#9a3412', marginBottom: 4 }}>{pauseLabel}</div>
          <div style={{ fontWeight: 700, color: '#9a3412' }}>{pauseMessage}</div>
        </div>
      )}

      <div className={Classes.body}>
        {/* Regular */}
        <div className={Classes.box}>
          <div className={Classes.boxHeader}>
            <GasPumpIcon fill={'var(--primary-color)'} height="25" />
            <h4>Mobile Fuel Delivery</h4>
          </div>
          <h5 className={Classes.title1}>Regular (87 Octane)</h5>
          <h5 className={Classes.text}>Fuel delivered to your vehicle</h5>
          <h5 className={Classes.title2}>{prices.regular}</h5>
          <button
            type="button"
            disabled={bookingsPaused}
            onClick={() => !bookingsPaused && handleNavigate('regular')}
            style={bookingsPaused ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
          >
            <h5>Purchase Regular (87 Octane)</h5>
            <ArrowNextIcon fill="white" height="22" />
          </button>
        </div>

        {/* Diesel */}
        <div className={Classes.box}>
          <div className={Classes.boxHeader}>
            <GasPumpIcon fill={'var(--primary-color)'} height="25" />
            <h4>Mobile Fuel Delivery</h4>
          </div>
          <h5 className={Classes.title1}>Diesel</h5>
          <h5 className={Classes.text}>Fuel delivered to your vehicle</h5>
          <h5 className={Classes.title2}>{prices.mid}</h5>
          <button
            type="button"
            disabled={bookingsPaused}
            onClick={() => !bookingsPaused && handleNavigate('mid')}
            style={bookingsPaused ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
          >
            <h5>Purchase Diesel</h5>
            <ArrowNextIcon fill="white" height="22" />
          </button>
        </div>

        {/* Premium */}
        <div className={Classes.box}>
          <div className={Classes.boxHeader}>
            <GasPumpIcon fill={'var(--primary-color)'} height="25" />
            <h4>Mobile Fuel Delivery</h4>
          </div>
          <h5 className={Classes.title1}>Premium (91-93 Octane)</h5>
          <h5 className={Classes.text}>Fuel delivered to your vehicle</h5>
          <h5 className={Classes.title2}>{prices.premium}</h5>
          <button
            type="button"
            disabled={bookingsPaused}
            onClick={() => !bookingsPaused && handleNavigate('premium')}
            style={bookingsPaused ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
          >
            <h5>Purchase Premium (91-93 Octane)</h5>
            <ArrowNextIcon fill="white" height="22" />
          </button>
        </div>
      </div>

      {/* ✅ Offer popup shown when booking during 24h window */}
      <OfferModal
        open={offerOpen}
        remainingText={isNewUserOffer ? remainingText : ''}
        onClose={() => {
          setOfferOpen(false);
          setPendingType(null);
        }}
        onViewMembership={() => {
          setOfferOpen(false);
          setPendingType(null);
          navigateTo('/settings/membership');
        }}
        onConfirm={() => {
          setOfferOpen(false);
          const t = pendingType || 'regular';
          setPendingType(null);
          goToAddAppointment(t);
        }}
      />
    </div>
  );
}
